import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth';
import { Loader2, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import logo from '../assets/images/logo.png';

const ForgotPassword = () => {
  const { toast } = useToast();
  const { forgotPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = () => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmail()) return;
    
    setLoading(true);
    
    const result = await forgotPassword(email);
    
    setLoading(false);
    
    if (result.success) {
      setSent(true);
      toast.success({
        title: 'Email Sent!',
        description: 'Check your inbox for password reset instructions'
      });
    } else {
      // Still show success for security (don't reveal if email exists)
      setSent(true);
      toast.success({
        title: 'Email Sent!',
        description: 'If this email exists, you will receive reset instructions'
      });
    }
  };

  // Success state
  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-3 shadow-lg">
                <img 
                  src={logo} 
                  alt="Climb Capital Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
            </div>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              We've sent password reset instructions to:
            </p>
            <p className="text-lg font-semibold text-gray-900 bg-gray-50 py-2 px-4 rounded-lg mb-6">
              {email}
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">📋 Next Steps:</h3>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Open the email we sent you</li>
                <li>Copy the User ID and Reset Token</li>
                <li>Click the button below to reset your password</li>
              </ol>
            </div>

            <Link
              to="/reset-password"
              className="block w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-center mb-4"
            >
              Enter Reset Code
            </Link>

            <button
              onClick={() => setSent(false)}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Didn't receive the email? Try again
            </button>
          </div>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-blue-600 hover:text-blue-800 font-semibold flex items-center justify-center gap-2"
            >
              <ArrowLeft size={18} />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-full p-3 shadow-lg">
              <img 
                src={logo} 
                alt="Climb Capital Logo" 
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password?</h1>
          <p className="text-gray-600">
            No worries! Enter your email and we'll send you reset instructions.
          </p>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  className={`w-full border-2 rounded-lg pl-12 pr-4 py-3 focus:outline-none transition-colors ${
                    error 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-gray-200 focus:border-yellow-400'
                  }`}
                  placeholder="Enter your registered email"
                  autoComplete="email"
                />
              </div>
              {error && (
                <p className="mt-1 text-sm text-red-500">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-blue-600 font-bold hover:text-blue-800 hover:underline"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-white/50 rounded-xl p-4 border border-gray-100">
          <p className="text-sm font-semibold text-gray-700 mb-2">Need help?</p>
          <p className="text-sm text-gray-600">
            If you're still having trouble, contact our support team on{' '}
            <a 
              href="https://api.whatsapp.com/send/?phone=919372273366&text=I%20need%20help%20with%20password%20reset"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 font-semibold hover:underline"
            >
              WhatsApp
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
